import type {
  HTTPTransaction,
  Headers,
  HeadersList,
  MockResponseModifier,
  Transaction,
  TransactionModifier,
} from '@carboncopyco/types';
import { decodeFromBase64 } from '../base64';
import {
  type HTTPContentTypeCategory,
  type HTTPMethod,
  type HTTPStatusCategory,
  contentTypeCategoryToMIMETypes,
  getContentType,
  getContentTypeCategory,
  getStatusCategory,
  isStatusInCategory,
} from '../http';

export const transactionsCountLimit = 2000;

export const supportedHTTPMethods = [
  'GET',
  'POST',
  'PUT',
  'PATCH',
  'DELETE',
] satisfies HTTPMethod[];

export type SupportedHTTPMethod = (typeof supportedHTTPMethods)[number];

export const supportedHTTPSStatusCategories = [
  'successful',
  'redirection',
  'clientError',
  'serverError',
] satisfies HTTPStatusCategory[];

export type SupportedHTTPStatusCategory =
  (typeof supportedHTTPSStatusCategories)[number];

// We don't support HTML content type, because then the user might remove important Demostack scripts.
// We don't support CSS and JS content types, because they're usually large transactions and the way-to-go should be custom code.
export const supportedContentTypeCategories = [
  'text',
  'json',
  'image',
] satisfies Exclude<HTTPContentTypeCategory, 'html' | 'css' | 'javascript'>[];

export type SupportedContentTypeCategory =
  (typeof supportedContentTypeCategories)[number];

export const contentTypeCategoryToLabel: Record<
  HTTPContentTypeCategory,
  string
> = {
  text: 'Text',
  json: 'JSON',
  image: 'Image',
  html: 'HTML',
  css: 'CSS',
  javascript: 'JS',
};

const isHTTPMethodSupported = (method: string): method is SupportedHTTPMethod =>
  supportedHTTPMethods.includes(method as any);

const isHTTPStatusSupported = (status: string | number) => {
  const statusCategory = getStatusCategory(status);
  return supportedHTTPSStatusCategories.includes(statusCategory as any);
};

const isHTTPContentTypeSupported = (contentType: string) => {
  const contentTypeCategory = getContentTypeCategory(contentType);
  return supportedContentTypeCategories.includes(contentTypeCategory as any);
};

export interface TransactionFilters {
  method?: SupportedHTTPMethod;
  statusCategory?: SupportedHTTPStatusCategory;
  responseContentType?: SupportedContentTypeCategory;
}

export const filterTransactions = <T extends Transaction>(
  transactions: T[],
  filters: TransactionFilters
) => {
  return transactions.filter(({ original }) => {
    const validations = [];

    if (filters.method) {
      validations.push(original.request.method === filters.method);
    }

    if (filters.statusCategory) {
      validations.push(
        isStatusInCategory(original.response.status, filters.statusCategory)
      );
    }

    if (filters.responseContentType) {
      const headers = convertHeadersToArray(original.response.headers);
      const contentType = getContentType(headers);

      validations.push(
        contentTypeCategoryToMIMETypes[filters.responseContentType].includes(
          contentType
        )
      );
    }

    return validations.every(Boolean);
  });
};

export const isSupportedTransaction = (transaction: HTTPTransaction) => {
  // We shouldn't support our own editor transactions
  const editorScriptNames = [
    'ds-editor-runtime',
    'ds-editor-player',
    'ds-editor-ui',
    'ds-editor-chunk',
    'demostack-banner',
    'dsrecorder.ui',
    'dsrecorder.desktop',
    'demostack.com/editor/images',
    'demostack.com/files/editor/images',
  ];

  if (
    editorScriptNames.some((name) => transaction.request.url.includes(name))
  ) {
    return false;
  }

  // We shouldn't support transactions with unsupported method, status or content type
  const headers = convertHeadersToArray(transaction.response.headers);
  const contentType = getContentType(headers);
  if (
    !isHTTPMethodSupported(transaction.request.method) ||
    !isHTTPStatusSupported(transaction.response.status) ||
    !isHTTPContentTypeSupported(contentType)
  ) {
    return false;
  }

  return true;
};

export const convertHeadersToArray = (headers: Headers): HeadersList => {
  return Object.entries(headers).flatMap(([name, headerValue]) =>
    headerValue.map((value) => ({ name, value }))
  );
};

export const convertHeadersToDict = (headers: HeadersList): Headers => {
  return headers.reduce((acc, { name, value }) => {
    if (!acc[name]) {
      acc[name] = [];
    }

    acc[name].push(value);

    return acc;
  }, {} as Headers);
};

export const isMockResponseModifier = (
  modifier: TransactionModifier
): modifier is MockResponseModifier => modifier.type === 'MockResponse';

export const isMockResponseEncoded = (
  modifier: MockResponseModifier,
  encodingType: MockResponseModifier['arguments']['contentEncoding']
) => modifier.arguments.contentEncoding === encodingType;

export const getNormlizedMockResponse = (
  modifier: MockResponseModifier,
  { filesServiceDomain }: { filesServiceDomain: string }
) => {
  const normalizedModifier = structuredClone(modifier);
  const { representationFieldsUrl, content } = normalizedModifier.arguments;

  if (representationFieldsUrl) {
    normalizedModifier.arguments.representationFieldsUrl =
      filesServiceDomain + representationFieldsUrl;
  }

  return isMockResponseEncoded(normalizedModifier, 'url')
    ? {
        ...normalizedModifier,
        arguments: {
          ...normalizedModifier.arguments,
          content: filesServiceDomain + content,
        },
      }
    : normalizedModifier;
};

export const normalizeTransaction = (transaction: Transaction) => {
  const { original } = transaction;

  const headers = convertHeadersToArray(transaction.original.response.headers);
  const contentTypeCategory = getContentTypeCategory(getContentType(headers));

  if (original?.request?.body) {
    original.request.body = decodeFromBase64(original.request.body);
  }

  if (original?.response?.body && contentTypeCategory !== 'image') {
    original.response.body = decodeFromBase64(original.response.body);
  }

  return transaction;
};
